import * as THREE from 'three';
import ActionManager from "./ActionManager";
import ARTracker from "./ARTracker";
import AssetsManager from "./AssetsManager";
import Engine from "./Engine";
import Scenario from "./scenarioElements/Scenario";
import SceneObjectUtils from "./utilities/SceneObjectUtils";
import ProjectPacker from './ProjectPacker';
import Utils from './Utilities';
import CreateTemplateFromStep from "../actions/ui/CreateTemplateFromStep";
import UITemplateTypes from "../components/uisystem/templates/types";
import { TrackerTypes, AssetTypes } from "../globals";
import { IUsedStepTemplate, UIVersions, BuiltInCubeMaps, IProjectMetadata, ITrackerMetaData, TracketTypeCount, AssetInfoProp } from "../interfaces/Misc";
import { IVisageTemplate } from "../interfaces/VisageUI";
import store from "../state/store";

export default class Metadata 
{
	static calculateProjectMetadata(blobSize: number)
	{
		const oldUi = { ...store.getState().uiModule.ui };
		const newUi = { ...store.getState().visageModule };
		const usedUITemplates = new Map<string, IUsedStepTemplate>();
		const steps = Scenario.getSteps();
		let oldTemplatesCount = 0;
		let newTemplatesCount = 0;
		let uiVersion = UIVersions.none;
		let totalObjectCount = 0;
		let totalPolyCount = 0;
		const objectTypes = new Set<string>();

		for(const step of steps)
		{
			// old ui
			if(oldUi[step.id] && oldUi[step.id].slide)
			{
				let templateAssetInfo = AssetsManager.getAssetInfo(oldUi[step.id].slide);

				if(!templateAssetInfo)
				{
					const action = new CreateTemplateFromStep(step, oldUi[step.id].slide);
					action.flagAsTemporary();
					ActionManager.removeAllTemporaryActions();
					templateAssetInfo = AssetsManager.getAssetInfo(oldUi[step.id].slide);
				}

				usedUITemplates.set(templateAssetInfo.id, {
					id: templateAssetInfo.id,
					name: templateAssetInfo.name,
					version: UIVersions.old10,
					isDefault: Object.values(UITemplateTypes).includes(templateAssetInfo.id),
				});

				oldTemplatesCount++;

				if(uiVersion === UIVersions.none)
				{
					uiVersion = UIVersions.old10;
				}
			}

			// new ui
			if(step.uiTemplateId && newUi.uiTemplateData[step.uiTemplateId])
			{
				const templateData: IVisageTemplate = newUi.uiTemplateData[step.uiTemplateId];
				usedUITemplates.set(templateData.id, {
					id: templateData.id,
					name: templateData.name,
					version: UIVersions.new20,
					isDefault: templateData.isDefault,
				});

				newTemplatesCount++;
				uiVersion = UIVersions.new20;
			}

			// object + poly counts
			step.scene.traverse((child: THREE.Object3D) =>
			{
				if(child.userData.hideInHierarchy || child.userData.ignoreExport) return;
				if(child.type === "Scene") return;

				totalObjectCount++;
				totalPolyCount += SceneObjectUtils.getObjectTriangleCount(child);
				objectTypes.add(SceneObjectUtils.getObjectClass(child));
			});
		}

		const templates = Array.from(usedUITemplates.values());

		// AR trackers
		const trackerTypes = new Set<TrackerTypes>();
		const trackerList = ARTracker.getTrackerList()
		for(const tracker of trackerList)
		{
			trackerTypes.add(tracker.trackerType);
		}
		const ar_tracker_type_count = {} as TracketTypeCount;
		trackerTypes.forEach(type => ar_tracker_type_count[type] = 0);
		//genearte counts
		for(const tracker of trackerList)
		{
			ar_tracker_type_count[tracker.trackerType]++;
		}
		
		const asset_info_props: AssetInfoProp[] = [{size: blobSize, type: AssetTypes.Model, name: "scenario.glb", id: "scenario.glb", is_revision: false}];
		
		Metadata.getProjectAssets(asset_info_props);
		
		const model_ar_trackers = Metadata.getTrackerData();
			
		const hdr = Engine.getInstance().envMapAssetId as BuiltInCubeMaps | BuiltInCubeMaps.Default;
		const output: IProjectMetadata = {
			ui_version: uiVersion,
			templates: [...templates],
			step_count: steps.length,
			ar_tracker_types: [...trackerTypes],
			skip_to_interactive_mode: false,
			object_types: [...objectTypes],
			object_count: totalObjectCount,
			poly_count: totalPolyCount,
			step_versions: {
				[UIVersions.old10]: oldTemplatesCount,
				[UIVersions.new20]: newTemplatesCount,
			},
			hdr: hdr,
			model_ar_trackers,
			ar_tracker_type_count,
			asset_info_props
		};

		console.log("project metadata: ", output);

		return output;
	}

	static isModelRevision = (type: AssetTypes) => 
	{
		if(type === AssetTypes.Revision ||  type === AssetTypes.RevisionProcessed || type === AssetTypes.ModelProcessed)
		{
			return AssetTypes.Model
		}
	}

	static getProjectAssets(asset_info_props: AssetInfoProp[]) 
	{
		const projectAssets = AssetsManager.getAssetsInProject();
		Object.values(projectAssets).forEach(assets => 
		{
			assets.forEach(assetId => 
			{
				const assetInfoProp = AssetsManager.getPropsForAsset(assetId);
				if (assetInfoProp) 
				{
					const assetInfo = AssetsManager.getAssetInfo(assetId);
					let name = `${Utils.getFileNameNoExtension(assetInfo.name)}`;
					const ext = `${Utils.getFileExtension(assetInfo.name)}`;
					if(ext)
					{
						name = `${name}.${ext.toLowerCase()}`;
					}
					const asset_info = {
						size: AssetsManager.getAssetSize(assetInfo) || 0,
						type: Metadata.isModelRevision(assetInfoProp.type) || assetInfoProp.type,
						id: assetInfoProp.assetId,
						name,
						is_revision: assetInfoProp.type === AssetTypes.Revision
					};
					asset_info_props.push(asset_info);
				}
			});
		});
	}

	static getTrackerData() 
	{
		return ARTracker.getTrackerList().map(tracker => 
		{
			const nodeIndex = ProjectPacker.getInstance().objectGuidToNodeIndexMap.get(tracker.modelTrackerCamera.userData.guid) ?? -1;
			const cameraIndex = ProjectPacker.getInstance().nodeIndexToCameraIndex.get(nodeIndex);
			const model_ar_tracker: ITrackerMetaData = {
				name: tracker.name,
				type: tracker.trackerType,
				displayMode: tracker.displayMode,
				guideImageAssetId: tracker.guideImageAssetId,
				guideSize: tracker.guideSize,
				autoInit: tracker.autoInit,
				advancedOptions: {
					laplaceThreshold: tracker.laplaceThreshold,
					normalThreshold: tracker.normalThreshold,
					minInlierRatioInit: tracker.minInlierRatioInit,
					minInlierRatioTracking: tracker.minInlierRatioTracking,
					lineGradientThreshold: tracker.lineGradientThreshold,
					lineSearchLengthInitRelative: tracker.lineSearchLengthInitRelative,
					lineSearchLengthTrackingRelative: tracker.lineSearchLengthTrackingRelative,
					keyFrameDistance: tracker.keyFrameDistance,
					fovPercent: tracker.fovPercent
				},
				initPoseCamera: cameraIndex || -1,
				ghostGuideNodeIndex: 173 // cant find this value
			};
			return model_ar_tracker;
		});
	}
}